// crewbit-admin/src/components/money/OutstandingAgingCard.tsx
//
// SPEC §Overview.7.2. Card surfacing the four aging buckets from the
// envelope's `outstanding.buckets`. Tones escalate: current=green,
// 1-30=neutral, 31-60=amber, 61+=red. Operators see severity at a glance
// without reading numbers.
//
// Bucket keys have leading digits → MUST use bracket access
// (`buckets['1_30']` not `buckets.1_30`).

import type { FinancialSummary } from '../../hooks/useFinancialSummary';
import { formatMoney } from '../../lib/money';
import { admin, palette, radii, shadow, spacing, transition } from '../../styles/tokens';

type Buckets = FinancialSummary['outstanding']['buckets'];

interface OutstandingAgingCardProps {
  buckets: Buckets;
  onClick?: () => void;
  // C3 — tapping a bucket row deep-links to the filtered Outstanding list.
  onBucketClick?: (key: keyof Buckets) => void;
}

interface RowSpec {
  key: keyof Buckets;
  label: string;
  tone: 'positive' | 'neutral' | 'amber' | 'red';
}

const ROWS: RowSpec[] = [
  { key: 'current', label: 'Current', tone: 'positive' },
  { key: '1_30', label: '1–30 days', tone: 'neutral' },
  { key: '31_60', label: '31–60 days', tone: 'amber' },
  { key: '61_plus', label: '61+ days', tone: 'red' },
];

interface ToneStyle {
  dot: string;
  amountColor: string;
}

function styleForTone(tone: RowSpec['tone']): ToneStyle {
  switch (tone) {
    case 'positive':
      return { dot: palette.green, amountColor: admin.header };
    case 'neutral':
      return { dot: palette.g300, amountColor: admin.header };
    case 'amber':
      return { dot: palette.amber, amountColor: palette.amber };
    case 'red':
      return { dot: palette.red, amountColor: palette.red };
  }
}

export function OutstandingAgingCard({ buckets, onClick, onBucketClick }: OutstandingAgingCardProps) {
  const total =
    buckets.current.amount +
    buckets['1_30'].amount +
    buckets['31_60'].amount +
    buckets['61_plus'].amount;

  const interactive = onClick !== undefined;
  const handleActivate = () => {
    if (onClick) onClick();
  };

  const interactiveProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
          }
        },
        'aria-label': 'Outstanding · aging — open Outstanding list',
      }
    : {};

  return (
    <div
      {...interactiveProps}
      style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        padding: spacing.lg,
        boxShadow: shadow.card,
        minHeight: 200,
        cursor: interactive ? 'pointer' : 'default',
        transition: interactive ? `box-shadow ${transition.fast}` : undefined,
      }}
      onMouseEnter={interactive ? (e) => (e.currentTarget.style.boxShadow = shadow.cardHover) : undefined}
      onMouseLeave={interactive ? (e) => (e.currentTarget.style.boxShadow = shadow.card) : undefined}
    >
      <div style={{ marginBottom: spacing.md }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: admin.header }}>
          Outstanding · aging
        </div>
        <div style={{ fontSize: 12, color: admin.caption, marginTop: 2 }}>
          {formatMoney(total)} across {buckets.current.count + buckets['1_30'].count + buckets['31_60'].count + buckets['61_plus'].count} invoice
          {buckets.current.count + buckets['1_30'].count + buckets['31_60'].count + buckets['61_plus'].count === 1 ? '' : 's'}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {ROWS.map((row, idx) => {
          const bucket = buckets[row.key];
          const tone = styleForTone(row.tone);
          const isLast = idx === ROWS.length - 1;
          return (
            <div
              key={row.key}
              role={onBucketClick ? 'button' : undefined}
              tabIndex={onBucketClick ? 0 : undefined}
              aria-label={onBucketClick ? `Outstanding ${row.label} — open filtered list` : undefined}
              onClick={
                onBucketClick
                  ? (e) => {
                      e.stopPropagation();
                      onBucketClick(row.key);
                    }
                  : undefined
              }
              onKeyDown={
                onBucketClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onBucketClick(row.key);
                      }
                    }
                  : undefined
              }
              onMouseEnter={
                onBucketClick ? (e) => (e.currentTarget.style.background = palette.accentWash) : undefined
              }
              onMouseLeave={
                onBucketClick ? (e) => (e.currentTarget.style.background = 'transparent') : undefined
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                paddingTop: 10,
                paddingBottom: 10,
                paddingLeft: onBucketClick ? 6 : 0,
                paddingRight: onBucketClick ? 6 : 0,
                marginLeft: onBucketClick ? -6 : 0,
                marginRight: onBucketClick ? -6 : 0,
                borderRadius: onBucketClick ? radii.sm : 0,
                borderBottom: isLast ? 'none' : `1px solid ${palette.g100}`,
                cursor: onBucketClick ? 'pointer' : 'default',
                transition: `background ${transition.fast}`,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: radii.full,
                  background: tone.dot,
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1, fontSize: 13.5, color: admin.body }}>
                {row.label}
              </span>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: tone.amountColor,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatMoney(bucket.amount)}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: admin.caption,
                  minWidth: 40,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {bucket.count} inv
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
